import { DateTime } from 'luxon';
import { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { BlueprintServerMetadata } from 'dashboard/models/blueprint';
import { BuilderName } from 'dashboard/models/inquiry-template';
import { ProductFeatureName } from 'dashboard/models/organization';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearMetrics } from 'dashboard/redux/actions/metrics-inquiry';
import { getInitialCasesFilters } from 'dashboard/redux/reducers/cases';
import { getInitialInquiriesFilterState } from 'dashboard/redux/reducers/inquiries';
import { RootState } from 'dashboard/redux/store';
import {
  addBlueprint,
  loadBlueprintPreview,
  loadBlueprints,
} from 'dashboard/redux/thunk-actions/blueprints';
import { loadCaseAnalyticsStatusDistributionForCurrentUser } from 'dashboard/redux/thunk-actions/case-analytics';
import { loadCaseTemplateBasicsOptions } from 'dashboard/redux/thunk-actions/case-templates';
import { loadCasesList } from 'dashboard/redux/thunk-actions/cases';
import { loadInquiryList } from 'dashboard/redux/thunk-actions/inquiries';
import { loadTemplateBuilderPreview } from 'dashboard/redux/thunk-actions/inquiry-templates';
import { loadMetricsInquiryRecentActivityVolume } from 'dashboard/redux/thunk-actions/metrics-inquiry';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import DashboardHomeView from 'dashboard/views/DashboardHomeView';
import { SegmentClient } from 'lib/clients/SegmentClient';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useEffectOnEnvironmentChange from 'lib/hooks/useOnEnvironmentChange';
import usePermission from 'lib/hooks/usePermission';
import useProductFeature from 'lib/hooks/useProductFeature';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isPermissionsError } from 'lib/permissions-errors';

const DashboardHomeRoute = (props: PropsFromRedux) => {
  // NB: We define all filtering, sorting, and pagination params in this route instead of pulling from the Redux state,
  // because we don't want filtered results on CasesHome and InquiriesHome to affect the data displayed on the homepage

  // Data loading for Cases table
  const casesLoader = () =>
    props.loadCasesList({
      currentEnvironment: props.currentEnvironment,
      currentPage: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      sortParams: {
        sort: 'created_at',
        shouldSortDescending: true,
      },
      // NOTE: these filters should match what is getting pulled from the analytics endpoint
      // at app/controllers/api/internal/dashboard/v1/metrics/cases_controller.rb#status_distribution_current_user
      filters: {
        ...getInitialCasesFilters(),
        assigneeIds: [props.currentUser.id],
        startDate: DateTime.local().minus({ months: 1 }).startOf('day').toISO(),
        endDate: DateTime.local().endOf('day').toISO(),
      },
    });

  const hasCasesProductFeature = useProductFeature(ProductFeatureName.CasesDashboard);

  const hasCaseAllViewPermission = usePermission(Permission.CaseAllView);
  const hasCaseListPermission = usePermission(Permission.CaseList);
  const hasCaseViewPermission = usePermission(Permission.CaseView);
  const hasAnyCaseViewPermission =
    hasCaseAllViewPermission || hasCaseListPermission || hasCaseViewPermission;

  const { loading: isCasesLoading } = useApiLoader({
    loader: casesLoader,
    deps: [],
    skip: !hasCasesProductFeature || !hasAnyCaseViewPermission,
    onError: (error) => {
      if (!isPermissionsError(error)) {
        props.showMessage('Failed to load Cases.', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      );
    },
  });

  const { loading: isCaseTagsLoading } = useApiLoader({
    loader: () => props.loadTags(ApiModel.Case),
    deps: [props.loadTags],
    skip: !hasCasesProductFeature || !hasAnyCaseViewPermission,
    onError: (error) => {
      if (!isPermissionsError(error)) {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // Data loading for Case Analytics
  const caseAnalyticsLoader = () =>
    props.loadCaseAnalyticsStatusDistributionForCurrentUser({
      currentEnvironment: props.currentEnvironment,
    });

  const { loading: isCaseAnalyticsLoading } = useApiLoader({
    loader: caseAnalyticsLoader,
    deps: [],
    skip: !hasCasesProductFeature || !hasAnyCaseViewPermission,
    onError: (error) => {
      if (!isPermissionsError(error)) {
        props.showMessage('Failed to load Case analytics.', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      );
    },
  });

  // Data loading for Case Templates (to get all Case States)
  const { loading: isCaseStatesLoading } = useApiLoader({
    loader: props.loadCaseTemplateBasicsOptions,
    deps: [props.loadCaseTemplateBasicsOptions],
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onError: null,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onNetworkError: null,
  });

  // Data loading for Inquiries table
  const inquiriesLoader = () =>
    props.loadInquiryList({
      currentEnvironment: props.currentEnvironment,
      currentPage: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      filters: getInitialInquiriesFilterState(),
      sortParams: {
        sort: 'created_at',
        shouldSortDescending: true,
      },
    });

  const hasInquiryListPermission = usePermission(Permission.InquiryList);
  const hasInquiryViewPermission = usePermission(Permission.InquiryView);

  const { loading: isInquiriesLoading } = useApiLoader({
    loader: inquiriesLoader,
    skip: !hasInquiryListPermission && !hasInquiryViewPermission,
    deps: [],
    onError: (error) => {
      if (!isPermissionsError(error)) {
        props.showMessage('Failed to load inquiries.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useEffectOnEnvironmentChange(() => props.clearMetrics());

  // Data loading for Inquiry Analytics
  const {
    callback: handleLoadInquiryAnalytics,
    loading: isInquiryAnalyticsLoading,
    error: loadingInquiryAnalyticsError,
  } = useApiCallback(async () => {
    if (props.inquiryAnalyticsVolume == null) {
      await props.loadMetricsInquiryRecentActivityVolume({
        currentEnvironment: props.currentEnvironment,
        filters: {
          createdAtStart: DateTime.local().minus({ days: 14 }).startOf('day').toISO(),
          createdAtEnd: DateTime.local().endOf('day').toISO(),
        },
      });
    }
  }, [
    props.currentEnvironment,
    props.inquiryAnalyticsVolume,
    props.loadMetricsInquiryRecentActivityVolume,
  ]);

  useApiErrorHandler({
    error: loadingInquiryAnalyticsError,
    onError: (error) => {
      if (!isPermissionsError(error)) {
        props.showMessage('Failed to load Inquiry analytics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // Data loading for the BlueprintsAllModal
  useApiLoader({
    loader: props.loadBlueprints,
    deps: [],
    onError: (error) => {
      if (!isPermissionsError(error)) {
        showMessage('Failed to load pre-built solutions.', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure);
    },
  });

  const [blueprintsCatalogServer, setBlueprintsCatalogServer] = useState(null);
  useApiLoader({
    loader: async () => {
      const data = await SessionClientDashboard.getResourceMeta<BlueprintServerMetadata[]>(
        ApiEndpoint.Blueprint,
        'catalog',
      );
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setBlueprintsCatalogServer(data.meta['blueprints-catalog']);
    },
    deps: [],
    onError: (error) => {
      if (!isPermissionsError(error)) {
        showMessage('Failed to load pre-built solution previews.', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      showMessage('There was an error loading this page. Please try again.', MessageType.Failure);
    },
  });

  const createBlueprint = async (blueprintKey: string) => {
    await props.addBlueprint(blueprintKey);

    SegmentClient.sendSegmentTrackEvent(
      'Blueprints All Modal (Production Home)  - Added Blueprint',
      {
        blueprintSourceType: blueprintKey,
      },
    );
  };

  const { callback: handleLoadBlueprintPreview, error: handleLoadBlueprintPreviewError } =
    useApiCallback(
      async (blueprintKey: string) => {
        await props.loadBlueprintPreview(blueprintKey);
      },
      [props.loadBlueprintPreview],
    );

  useApiErrorHandler({
    error: handleLoadBlueprintPreviewError,
    onError: (error) => {
      if (!isPermissionsError(error)) {
        showMessage('Failed to load solution preview.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const {
    callback: handleLoadInquiryTemplateBuilderPreview,
    error: handleLoadInquiryTemplateBuilderPreviewError,
  } = useApiCallback(
    async (inquiryTemplateBuilderName: BuilderName) => {
      await props.loadInquiryTemplateBuilderPreview(inquiryTemplateBuilderName);
    },
    [props.loadInquiryTemplateBuilderPreview],
  );

  useApiErrorHandler({
    error: handleLoadInquiryTemplateBuilderPreviewError,
    onError: (error) => {
      if (!isPermissionsError(error)) {
        showMessage('Failed to load inquiry template preview.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const caseStates = props.caseTemplateBasicsOptions.map((ct) => {
    return {
      states: ct.states,
      initialStateToken: ct['initial-state-token'],
    };
  });

  return (
    <DashboardHomeView
      cases={props.cases}
      caseUsers={props.caseUsers}
      caseCaseQueues={props.caseCaseQueues}
      caseTags={props.caseTags}
      caseAccounts={props.caseAccounts}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      caseStatusDistributionForCurrentUser={props.caseStatusDistributionForCurrentUser}
      caseStates={caseStates}
      currentOrganizationPlan={props.currentOrganizationPlan}
      isCasesLoading={isCasesLoading}
      isCaseAnalyticsLoading={isCaseAnalyticsLoading}
      isCaseStatesLoading={isCaseStatesLoading}
      isCaseTagsLoading={isCaseTagsLoading}
      inquiries={props.inquiries}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inquiryAnalyticsVolume={props.inquiryAnalyticsVolume}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inquiryAnalyticsVolumeDelta={props.inquiryAnalyticsVolumeDelta}
      isInquiriesLoading={isInquiriesLoading}
      handleLoadInquiryAnalytics={handleLoadInquiryAnalytics}
      isInquiryAnalyticsLoading={isInquiryAnalyticsLoading}
      previouslyUsedBlueprints={props.blueprints}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      blueprintsCatalogServer={blueprintsCatalogServer}
      blueprintPreviews={props.blueprintPreviews}
      inquiryTemplateBuilderPreviews={props.inquiryTemplateBuilderPreviews}
      organization={props.organization}
      createBlueprint={createBlueprint}
      handleLoadBlueprintPreview={handleLoadBlueprintPreview}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleLoadInquiryTemplateBuilderPreview={handleLoadInquiryTemplateBuilderPreview}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    blueprints: state.blueprints.blueprints,
    blueprintPreviews: state.blueprints.blueprintPreviews,
    cases: state.cases.caseRequestKey ? state.cases.cases[state.cases.caseRequestKey] || [] : [],
    caseTags: state.tags.tags.case,
    caseAccounts: state.cases.accounts,
    caseCaseQueues: state.cases.caseQueues,
    caseUsers: state.cases.users,
    caseStatusDistributionForCurrentUser: state.caseAnalytics.caseStatusDistributionForCurrentUser,
    caseTemplates: state.caseAnalytics.caseTemplates,
    caseTemplateBasicsOptions: state.caseTemplates.basicsOptions,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentOrganizationPlan: state.organization.currentOrganizationPlan,
    currentUser: state.currentUser.currentUser,
    inquiries: state.inquiries.inquiries[state.currentEnvironment.currentEnvironment.id] || [],
    inquiryAnalyticsVolume: state.metricsInquiry.recentActivityVolume,
    inquiryAnalyticsVolumeDelta: state.metricsInquiry.recentActivityVolumeDelta,
    inquiryTemplateBuilderPreviews: state.inquiryTemplates.templateBuilderPreviews,
    organization: state.organization.organization,
  };
};

const mapDispatchToProps = {
  addBlueprint,
  clearMetrics,
  loadBlueprints,
  loadBlueprintPreview,
  loadCasesList,
  loadCaseTemplateBasicsOptions,
  loadCaseAnalyticsStatusDistributionForCurrentUser,
  loadInquiryList,
  loadInquiryTemplateBuilderPreview: loadTemplateBuilderPreview,
  loadMetricsInquiryRecentActivityVolume,
  loadTags,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardHomeRoute);
